import React, { useState } from "react";

/**
 * Avatar — renders a user's display picture (DP) if `src` is set,
 * otherwise falls back to a colored circle with the first initial.
 *
 * Single source of truth for every avatar in the app so the DP shows
 * consistently across tenant / service / landlord surfaces. Replaces
 * the ad-hoc `{name[0]}` circles that were scattered across ~8 files.
 *
 * Props:
 *   src    - image URL (avatar_url). Falsy → initial fallback.
 *   name   - used for the initial + deterministic fallback color.
 *   size   - diameter in px (default 40).
 *   bg     - override fallback background color (else derived from name).
 *   color  - initial text color (default #fff).
 *   fontSize - override initial font size (else ~45% of size).
 *   ring   - optional border (e.g. "2px solid #fff").
 *   style  - extra style overrides merged last.
 */

// Deterministic color from a string so the same person always gets the
// same fallback tone. Palette kept readable on white initials.
const PALETTE = ["#3b82f6", "#16a34a", "#d97706", "#9333ea", "#ef4444", "#0891b2", "#db2777"];
export function avatarColor(name) {
  const s = String(name || "").trim();
  if (!s) return PALETTE[0];
  let sum = 0;
  for (let i = 0; i < s.length; i++) sum += s.charCodeAt(i);
  return PALETTE[sum % PALETTE.length];
}

export default function Avatar({ src, name, size = 40, bg, color = "#fff", fontSize, ring, style = {} }) {
  const [imgFailed, setImgFailed] = useState(false);
  const initial = String(name || "?").trim().charAt(0).toUpperCase() || "?";
  const fallbackBg = bg || avatarColor(name);
  const showImg = !!src && !imgFailed;

  const base = {
    width: size,
    height: size,
    borderRadius: "50%",
    flexShrink: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...(ring ? { border: ring } : {}),
    ...style,
  };

  if (showImg) {
    return (
      <div style={base}>
        <img
          src={src}
          alt={name || "avatar"}
          onError={() => setImgFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>
    );
  }

  return (
    <div style={{ ...base, background: fallbackBg, color, fontWeight: 700, fontSize: fontSize || Math.round(size * 0.45) }}>
      {initial}
    </div>
  );
}
